/**
 * Parse a Claude-generated training module HTML and return:
 *   { moduleTitle, sections: [{ id, title, element }] }
 *
 * Detection strategy:
 *   1. Prefer <nav class="toc"> with <a> links → resolve to elements by href #id.
 *   2. Fallback to all <h2> in document order → each section's element is the
 *      h2 plus everything until the next h2 (a "section span").
 *   3. If neither yields any sections, throw.
 *
 * The returned `element` is always a real Node in the input document; the caller
 * uses it for capture-time DOM cloning.
 */
export function parseModule(doc) {
  const moduleTitle = (doc.querySelector('h1')?.textContent || doc.title || 'Untitled module').trim();

  // Strategy 1: nav.toc
  const navLinks = doc.querySelectorAll('nav.toc a[href^="#"], nav[role="navigation"] a[href^="#"]');
  if (navLinks.length > 0) {
    const sections = [];
    navLinks.forEach((a) => {
      const href = a.getAttribute('href') || '';
      const id = href.slice(1);
      const target = id ? doc.getElementById(id) : null;
      if (target) {
        sections.push({
          id,
          title: a.textContent.trim(),
          element: target
        });
      }
    });
    if (sections.length > 0) return { moduleTitle, sections };
  }

  // Strategy 2: h2 fallback (gather h2..next-h2 span as a synthetic section)
  const h2s = Array.from(doc.querySelectorAll('h2'));
  if (h2s.length > 0) {
    const sections = h2s.map((h2, idx) => ({
      id: h2.id || `auto-${idx}`,
      title: h2.textContent.trim(),
      element: synthSectionFrom(h2, h2s[idx + 1])
    }));
    return { moduleTitle, sections };
  }

  throw new Error('No sections detected: expected <nav class="toc"> links or <h2> headings.');
}

/**
 * Build a DocumentFragment-like element containing nodes from `start` (inclusive)
 * up to but not including `end`. Used by the h2 fallback strategy.
 */
function synthSectionFrom(start, end) {
  const wrapper = start.ownerDocument.createElement('section');
  let node = start;
  while (node && node !== end) {
    const next = node.nextSibling;
    wrapper.appendChild(node.cloneNode(true));
    node = next;
    if (node?.nodeType === 1 && node === end) break;
  }
  return wrapper;
}
